import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, ArrowRight, CheckCircle, FlaskConical, Play } from "lucide-react";

interface ChallengeStatusManagerProps {
  challengeId: string;
  currentStatus: string;
  onStatusChange: () => void;
}

const STATUS_OPTIONS = [
  { value: "in_progress", label: "In Progress", icon: Play },
  { value: "testing", label: "Testing", icon: FlaskConical },
  { value: "complete", label: "Complete", icon: CheckCircle },
];

const ChallengeStatusManager: React.FC<ChallengeStatusManagerProps> = ({
  challengeId,
  currentStatus,
  onStatusChange,
}) => {
  const [updating, setUpdating] = useState(false);

  const updateStatus = async (newStatus: string) => {
    if (newStatus === currentStatus) return;

    setUpdating(true);
    try {
      const updates: any = {
        status: newStatus,
      };

      if (newStatus === "complete") {
        updates.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from("challenges")
        .update(updates)
        .eq("id", challengeId);

      if (error) throw error;

      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      onStatusChange();
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const availableStatuses = STATUS_OPTIONS.filter(
    (s) => s.value !== currentStatus && s.value !== "unclaimed"
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={updating} className="gap-2">
          <ArrowRight className="h-4 w-4" />
          Move to
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {availableStatuses.map((status) => (
          <DropdownMenuItem
            key={status.value}
            onClick={() => updateStatus(status.value)}
            className="gap-2"
          >
            <status.icon className="h-4 w-4" />
            {status.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ChallengeStatusManager;
